import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FeaturePermissionEntityInterface, PagePermissionEntityInterface } from '@types';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import MultipleLimitTagsAutocomplete, {
  IMultipleLimitTagsAutocomplete,
} from '@sharedComponents/MultipleLimitTagsAutocomplete';
import { Checkbox } from '@sharedComponents/Checkbox';
import { getAllSubmissionGroupListSelector } from '@selectors/submissionGroups';
import { ConfigurationRadioGroup } from '@components/Pages/DashboardPage/RolesPage/RoleConfigurationList/PagePermissionsAccordion/FeatureConfigurationList/ConfigurationRadioGroup';
import { PermissionActionsNames } from '@constants/permissions';
import { setCurrentRolePageGroupFeatureActionPermissions } from '@actions/roleActions';
import { translate } from '@utils';
import { PermissionActions } from '@enums';

interface Props {
  pagePermission: PagePermissionEntityInterface;
  features: FeaturePermissionEntityInterface[];
  externalFeature?: FeaturePermissionEntityInterface;
}

const FeatureConfigurationGroupItem = ({ pagePermission, features, externalFeature }: Props) => {
  const [groupOptions, setGroupOptions] = useState<IMultipleLimitTagsAutocomplete[]>([]);

  const groups = useSelector(getAllSubmissionGroupListSelector);
  const dispatch = useDispatch();

  const handleRemoveGroup = (option: IMultipleLimitTagsAutocomplete) => {
    dispatch(setCurrentRolePageGroupFeatureActionPermissions(pagePermission.id, option.id, false));
  };

  const handleSelectGroup = (option: IMultipleLimitTagsAutocomplete) => {
    dispatch(setCurrentRolePageGroupFeatureActionPermissions(pagePermission.id, option.id, true));
  };

  useEffect(() => {
    if (groups?.length && features) {
      const data = groups.map((group) => {
        const currentFeature = features.find((feature) => feature.instanceKey === group.id);
        return {
          id: group.id,
          name: group.name,
          selected: currentFeature ? currentFeature[PermissionActions.View] : true,
        } as IMultipleLimitTagsAutocomplete;
      });
      if (data.length) {
        setGroupOptions(data);
      }
    }
  }, [groups]);

  const externalFeatureActions = Object.keys(PermissionActionsNames);
  return (
    <div className="feature-configuration-item" data-testid="feature-configuration-item">
      <Accordion elevation={0} expanded data-testid={`feature-container-groups`}>
        <AccordionSummary data-testid={`feature-title-groups`}>
          <div className="feature-configuration-item-name">
            <Checkbox checked disabled />
            <span className="feature-configuration-title">Groups</span>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="feature-configuration-item-content">
            <div>
              <div className="feature-configuration-action-container">
                {groupOptions.length && (
                  <MultipleLimitTagsAutocomplete
                    onRemoveOption={handleRemoveGroup}
                    onSelectOption={handleSelectGroup}
                    options={groupOptions}
                    errorHelper={translate('group_multi_autocomplete_error')}
                  />
                )}
              </div>
              <div data-testid="feature-group-list-actions">
                {externalFeatureActions.map((action) => {
                  return (
                    externalFeature[action] !== null && (
                      <div
                        key={`${externalFeature.id}-${action}`}
                        className="feature-configuration-action-container"
                      >
                        <ConfigurationRadioGroup
                          pagePermission={pagePermission}
                          feature={externalFeature}
                          action={action}
                        />
                      </div>
                    )
                  );
                })}
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export { FeatureConfigurationGroupItem };
